/**
 * useRole — Checks the current user's role (admin, moderator, user) via Supabase.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase, withSessionSetup } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { UserRole } from '@/utils/securityUtils';

export function useRole() {
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const checkRole = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

      // Check role through secure RPC function (uses SECURITY DEFINER to bypass RLS)
      try {
        const { data, error } = await supabase.rpc('get_user_role', { user_id_param: user.id });
        
        if (error) {
          // Don't throw - just set role to null for non-admin users
          setRole(null);
        } else {
          setRole(data || null);
        }
      } catch (rpcError) {
        // Don't throw - just set role to null for non-admin users
        setRole(null);
      }
    } catch (error) {
      // Don't show toast for role errors - many users won't have roles
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkRole();

    // Subscribe to role changes to update UI when admin assigns roles
    const channel = supabase
      .channel('user_roles_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles',
        },
        () => {
          checkRole();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [checkRole]);

  return { role, loading, checkRole };
}
